import type { ReactNode } from 'react';
import { Alert, Button, Center, Loader, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { errorMessage } from '@/api/http';

export function ErrorAlert({
  error,
  title = "That didn't work",
  onRetry,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
}) {
  return (
    <Alert
      color="coral"
      variant="light"
      icon={<IconAlertTriangle size={18} />}
      title={title}
      role="alert"
    >
      <Stack gap="xs" align="flex-start">
        <Text size="sm">{errorMessage(error)}</Text>
        {onRetry && (
          <Button size="xs" variant="default" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <Center py="xl" aria-live="polite" aria-busy="true">
      <Stack align="center" gap="xs">
        <Loader size="sm" color="sky" />
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Stack>
    </Center>
  );
}

interface QueryLike<T> {
  data: T | undefined;
  error: unknown;
  isPending: boolean;
  refetch: () => unknown;
}

/** Render loading / error states for a query, and children once data is there. */
export function QueryState<T>({
  query,
  children,
  loadingLabel,
}: {
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  loadingLabel?: string;
}) {
  if (query.isPending) return <PageLoader label={loadingLabel} />;
  if (query.error || query.data === undefined)
    return (
      <ErrorAlert
        error={query.error}
        title="Couldn't load this"
        onRetry={() => void query.refetch()}
      />
    );
  return <>{children(query.data)}</>;
}
